import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { impersonationService } from '../services';
import { authService } from '../services';
import api from '../services/api';
import { useAuth } from './AuthContext';

/**
 * Impersonation Context - Phase 2.6B / Demo Mode
 * Manages "View as Client" impersonation state across the app.
 * Also controls anonymised demo mode: a display-layer-only toggle that
 * replaces identifiable names with deterministic placeholders.
 */

const ImpersonationContext = createContext(null);

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
};

// ---------------------------------------------------------------------------
// Anonymise helper (deterministic, session-scoped)
// ---------------------------------------------------------------------------
/**
 * Given an original value and a semantic type, returns a stable placeholder
 * for the duration of the current impersonation session.
 *
 * type → placeholder pattern:
 *   'site'    → Demo Site 01, Demo Site 02 …
 *   'org'     → Demo Group  (always singular — one client per session)
 *   'group'   → Demo Group  (same as org; client-level grouping label)
 *   'region'  → Demo Region 01, Demo Region 02 …
 *   'user'    → Demo User 01, Demo User 02 …
 *   'email'   → demo.user01@example.com …
 *   'contact' → Demo Contact 01 …
 */
const buildAnonymise = (mappingRef, countersRef) => (value, type = 'site') => {
  if (!value) return value;
  const key = `${type}:${value}`;
  if (mappingRef.current.has(key)) return mappingRef.current.get(key);

  const count = (countersRef.current[type] || 0) + 1;
  countersRef.current[type] = count;
  const pad = String(count).padStart(2, '0');

  let placeholder;
  switch (type) {
    case 'site':    placeholder = `Demo Site ${pad}`; break;
    case 'org':     placeholder = 'Demo Group'; break;
    case 'group':   placeholder = 'Demo Group'; break;
    case 'region':  placeholder = `Demo Region ${pad}`; break;
    case 'user':    placeholder = `Demo User ${pad}`; break;
    case 'email':   placeholder = `demo.user${pad}@example.com`; break;
    case 'contact': placeholder = `Demo Contact ${pad}`; break;
    default:        placeholder = `Demo Item ${pad}`;
  }

  mappingRef.current.set(key, placeholder);
  return placeholder;
};

export const ImpersonationProvider = ({ children }) => {
  const [impersonation, setImpersonation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expired, setExpired] = useState(false);
  const [isAnonymised, setIsAnonymised] = useState(false);
  const expiryTimerRef = useRef(null);
  // Per-session mapping tables (reset on each new impersonation)
  const mappingRef  = useRef(new Map());
  const countersRef = useRef({});

  // Auth context refresh — keeps frontend role-state in sync with the auditor's
  // effective profile.  Stored in a ref so the expiry-timer callback always
  // calls the latest version without triggering re-renders.
  const { refreshUser } = useAuth();
  const refreshUserRef = useRef(refreshUser);
  useEffect(() => { refreshUserRef.current = refreshUser; }, [refreshUser]);

  const checkImpersonation = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setImpersonation(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await impersonationService.getCurrentImpersonation();
      setImpersonation(data);
      setExpired(false);
      if (data && data.remaining_minutes > 0) {
        if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = setTimeout(() => {
          setExpired(true);
          setImpersonation(null);
          setIsAnonymised(false);
          // Restore auth context when an auditor-view session auto-expires
          refreshUserRef.current?.();
        }, data.remaining_minutes * 60 * 1000);
      }
    } catch {
      setImpersonation(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkImpersonation();
    return () => { if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current); };
  }, [checkImpersonation]);

  const startImpersonation = async (clientUserId) => {
    setLoading(true);
    mappingRef.current = new Map();
    countersRef.current = {};
    setIsAnonymised(false);
    try {
      const data = await impersonationService.startImpersonation(clientUserId);
      await checkImpersonation();
      return data;
    } finally {
      setLoading(false);
    }
  };

  const startAuditorView = async (auditorUserId) => {
    setLoading(true);
    mappingRef.current = new Map();
    countersRef.current = {};
    setIsAnonymised(false);
    try {
      const data = await impersonationService.startAuditorView(auditorUserId);
      await checkImpersonation();
      // Refresh the auth context so the frontend reflects the auditor's effective
      // role/scope (backend now returns auditor profile via get_current_user).
      // This prevents admin-only UI controls from appearing during the session.
      await refreshUserRef.current?.();
      return data;
    } finally {
      setLoading(false);
    }
  };

  // Derived: are we in auditor-view mode?
  const isAuditorView = !!(impersonation && impersonation.session_type === 'auditor');
  const isClientView  = !!(impersonation && impersonation.session_type === 'client');

  const endImpersonation = async () => {
    // Capture session type before clearing state so we can restore auth afterwards
    const wasAuditorView = !!(impersonation && impersonation.session_type === 'auditor');
    setLoading(true);
    // Log deactivation if currently anonymised
    if (isAnonymised) {
      try { await api.post('/admin/impersonation/log-anonymise', { enabled: false }); } catch { /* non-fatal */ }
    }
    setIsAnonymised(false);
    mappingRef.current = new Map();
    countersRef.current = {};
    try {
      await impersonationService.endImpersonation();
      setImpersonation(null);
      setExpired(false);
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      // Restore admin auth context — the impersonation cookie is now cleared, so
      // /api/auth/me returns the real admin profile again.
      if (wasAuditorView) {
        await refreshUserRef.current?.();
      }
    } catch {
      setImpersonation(null);
      setExpired(false);
      if (wasAuditorView) {
        try { await refreshUserRef.current?.(); } catch { /* best-effort */ }
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle anonymised demo mode.
   * Only callable when an impersonation session is active.
   * Logs the event to the backend audit trail.
   */
  const toggleAnonymised = useCallback(async () => {
    if (!impersonation) return;
    const next = !isAnonymised;
    setIsAnonymised(next);
    try {
      await api.post('/admin/impersonation/log-anonymise', { enabled: next });
    } catch { /* audit logging is best-effort; toggle still applies */ }
  }, [impersonation, isAnonymised]);

  /**
   * anonymise(value, type) — returns placeholder when demo mode is ON,
   * otherwise returns the original value unchanged.
   */
  const anonymise = useCallback((value, type = 'site') => {
    if (!isAnonymised) return value;
    return buildAnonymise(mappingRef, countersRef)(value, type);
  }, [isAnonymised]);

  const value = {
    impersonation,
    isImpersonating: !!impersonation,
    isAuditorView,
    isClientView,
    loading,
    expired,
    isAnonymised,
    toggleAnonymised,
    anonymise,
    startImpersonation,
    startAuditorView,
    endImpersonation,
    refresh: checkImpersonation,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export default ImpersonationContext;

